import {
  GROUP_TYPES,
  FACILITY_TYPE,
  SEARCH_OBJECT_TYPES,
  CARD_TYPES,
} from "features/planner/planner.constants";

import { createSelector } from "utils/app.util";
import { EMPTY_MAP, EMPTY_LIST } from "app/app.constants";
import { FieldMapper, FacilityMapper } from "features/planner/planner.mappers";

const getGroupSelector = (state, groupId) =>
  state.getIn(["groups", groupId.toString()]);

const getViewsSelector = state => state.getIn(["view"]);

const getCardSelector = (state, cardId) =>
  state.getIn(["cards", cardId.toString()]);

export const getCardFromState = createSelector(getCardSelector, card => card);

export const getViewsFromState = createSelector(getViewsSelector, views =>
  views
    ? views
        .delete("defaultViewId")
        .toList()
        .sortBy(view => view.get("name"))
    : undefined,
);

const getViewGroupsSelector = state => state.getIn(["viewGroups", "byId"]);

export const getViewGroupsFromState = createSelector(
  getViewGroupsSelector,
  viewGroups => viewGroups,
);

export const getViewGroupByGroupIdFromState = createSelector(
  getViewGroupsSelector,
  (state, groupId) => groupId,
  (viewGroups, groupId) =>
    viewGroups
      .filter(viewGroup => viewGroup.get("groupId"))
      .groupBy(viewGroup => viewGroup.get("groupId").toString())
      .get(groupId.toString(), EMPTY_MAP)
      .first(),
);

const getGroupSearchResults = state =>
  state
    .getIn(["search", "hits"])
    .filter(hit => hit.get("objectType") === SEARCH_OBJECT_TYPES.GROUP);

const getCardSearchResults = state =>
  state
    .getIn(["search", "hits"])
    .filter(hit => hit.get("objectType") === SEARCH_OBJECT_TYPES.CARD);

const getSearchTermSelector = state => state.getIn(["search", "query", "term"]);

export const getSearchTermFromState = createSelector(
  getSearchTermSelector,
  searchTerm => searchTerm,
);

export const getViewGroupFromState = createSelector(
  (state, viewGroupId) =>
    state.getIn(["viewGroups", "byId", viewGroupId.toString()]),
  viewGroup => viewGroup,
);

const getCardContainerSelector = (state, groupId, cardContainerId) =>
  state.getIn([
    "groups",
    groupId.toString(),
    "cardContainersById",
    cardContainerId.toString(),
  ]);

export const getCardContainerFromState = createSelector(
  getCardContainerSelector,
  cardContainer => cardContainer,
);

const getMarkerSelector = (state, markerId) =>
  state.getIn(["markers", markerId.toString()]);

export const getMarkerFromState = createSelector(
  getMarkerSelector,
  marker => marker,
);

const getMarkersSelector = state => state.getIn(["markers"]);

export const getMarkersFromState = createSelector(getMarkersSelector, markers =>
  markers.toList().sortBy(marker => marker.get("name").toLowerCase()),
);

const getGroupsSelector = state => state.getIn(["groups"]);

export const getGroupsFromState = createSelector(getGroupsSelector, groups =>
  groups.toList().sortBy(group => group.get("name").toLowerCase()),
);

const getFieldsSelector = state => state.getIn(["npd", "fields"]);

const getCountriesSelector = state => state.getIn(["countries"]);

export const getCountriesFromState = createSelector(
  getCountriesSelector,
  countries =>
    countries.toList().sortBy(country => country.get("name").toLowerCase()),
);

export const getFieldsFromState = createSelector(
  getFieldsSelector,
  getCountriesSelector,
  (fields, countries) =>
    fields
      .toList()
      .map(field => {
        const countryId = field.get("countryId");

        return countryId
          ? field.set("country", countries.get(countryId.toString()))
          : field;
      })
      .sortBy(field => field.get("name").toLowerCase()),
);

export const getGroupFromState = createSelector(
  getGroupSelector,
  group => group,
);

const getStationaryFacilitiesSelector = state =>
  state.getIn(["npd", "facilities", FACILITY_TYPE.FIXED.toString()]);

const getMobileFacilitiesSelector = state =>
  state.getIn(["npd", "facilities", FACILITY_TYPE.MOBILE.toString()]);

export const getMobileFacilitiesFromState = createSelector(
  getMobileFacilitiesSelector,
  mobileFacilities =>
    mobileFacilities
      .toList()
      .sortBy(facility => facility.get("name").toLowerCase()),
);

export const getStationaryFacilitiesFromState = createSelector(
  getStationaryFacilitiesSelector,
  getFieldsFromState,
  (stationaryFacilities, fields) => {
    return stationaryFacilities
      .map(facility => {
        const fieldId = facility.get("fieldId");

        return facility.set(
          "fields",
          fieldId ? fields.get(fieldId) : FieldMapper.initial(),
        );
      })
      .toList()
      .sortBy(facility => facility.get("name"));
  },
);

export const getStationaryAndMobileFacilitiesFromState = createSelector(
  state => state.getIn(["npd", "facilities"]),
  facilities => facilities,
);

export const getStationaryFacilitiesByFieldIdFromState = createSelector(
  getStationaryFacilitiesSelector,
  (state, fieldId) => fieldId,
  (stationaryFacilities, fieldId) =>
    stationaryFacilities
      .filter(facility => {
        const facilityFieldId = facility.get("fieldId");

        return (
          fieldId &&
          facilityFieldId &&
          facilityFieldId.toString() === fieldId.toString()
        );
      })
      .toList()
      .sortBy(facility => facility.get("name")),
);

const getQualificationsSelector = state => state.getIn(["qualifications"]);

const getQualificationSelector = (state, qualificationId) =>
  state.getIn(["qualifications", qualificationId.toString()]);

export const getQualificationsFromState = createSelector(
  getQualificationsSelector,
  qualifications =>
    qualifications.toList().sortBy(qualification => qualification.get("name")),
);

export const getQualificationByIdFromState = createSelector(
  getQualificationSelector,
  qualification => qualification,
);

export const getQualificationByCardIdFromState = createSelector(
  getQualificationsSelector,
  getCardSelector,
  (qualifications, card) => {
    if (!card) return undefined;

    const qualificationId = card.getIn(["employee", "qualificationId"]);

    return qualificationId
      ? qualifications.get(qualificationId.toString())
      : undefined;
  },
);

const getCrewsSelector = state => state.getIn(["crews"]);

export const getCrewsFromState = createSelector(getCrewsSelector, crews =>
  crews.toList().sortBy(crew => crew.get("name")),
);

const getCompaniesSelector = state => state.getIn(["company"]);

export const getCompaniesFromState = createSelector(
  getCompaniesSelector,
  companies => companies.toList().sortBy(company => company.get("name")),
);

const getCompanySelector = (state, companyId) =>
  state.getIn(["company", companyId.toString()]);

export const getCompanyFromState = createSelector(
  getCompanySelector,
  company => company,
);

const getReplaceableEmployeesSelector = (state, groupId) =>
  state.getIn(["replaceableEmployees", groupId.toString()]);

export const getReplaceableEmployeesFromState = createSelector(
  getReplaceableEmployeesSelector,
  replaceableEmployees =>
    replaceableEmployees
      ? replaceableEmployees
          .toList()
          .sortBy(employee => employee.get("displayName"))
      : undefined,
);

export const getCardsSelector = state => state.getIn(["cards"]);

export const getCardsFromState = createSelector(getCardsSelector, cards =>
  cards.filter(card => !card.getIn(["employee", "hideInPlanning"])),
);

export const getCardSearchResultsFromState = createSelector(
  getCardSearchResults,
  getCardsSelector,
  (searchResults, cards) =>
    searchResults
      .map(searchResult => cards.get(searchResult.get("objectId").toString()))
      .filter(Boolean)
      .sortBy(card => card.get("type")),
);

export const getGroupSearchResultsFromState = createSelector(
  getGroupSearchResults,
  getGroupsSelector,
  (searchResults, groups) =>
    searchResults
      .map(searchResult => groups.get(searchResult.get("objectId").toString()))
      .filter(Boolean)
      .sortBy(group => group.get("name")),
);

const getDeploymentDetailSelector = (state, deploymentDetailId) =>
  state.getIn(["deploymentDetails", deploymentDetailId.toString()]);

const getDeploymentDetailsByCardIdSelector = (state, cardId) =>
  state.getIn(["deploymentDetails", "byCardId", cardId.toString()]);

export const getDeploymentDetailByCardIdFromState = createSelector(
  (state, cardId) => {
    const deploymentDetailIds = getDeploymentDetailsByCardIdSelector(
      state,
      cardId,
    );
    const lastDeploymentDetailId =
      deploymentDetailIds && deploymentDetailIds.sortBy(d => d.arrival).last();
    return getDeploymentDetailSelector(state, lastDeploymentDetailId || 0);
  },
  deploymentDetail => deploymentDetail,
);

export const getDeploymentDetailsByCardIdFromState = createSelector(
  (state, cardId) => {
    const deploymentDetailIds = getDeploymentDetailsByCardIdSelector(
      state,
      cardId,
    );
    return (
      deploymentDetailIds &&
      deploymentDetailIds.map(id => getDeploymentDetailSelector(state, id))
    );
  },
  deploymentDetails =>
    deploymentDetails ? deploymentDetails.toList() : undefined,
);

export const getWebSocketFromState = createSelector(
  (state, viewId) => state.getIn(["webSocket", viewId.toString(), "webSocket"]),
  webSocket => webSocket,
);

export const getWebSocketDidErrorFromState = createSelector(
  (state, viewId) =>
    state.getIn(["webSocket", viewId.toString(), "webSocketDidError"]),
  value => value,
);

export const getCardsByCardContainerAndSlotNo = createSelector(
  getCardsFromState,
  cards =>
    cards
      .sortBy(card => -card.get("type"))
      .reduce(
        (result, card) =>
          result.setIn(
            [
              card.get("cardContainerId").toString(),
              card.get("slotNo").toString(),
            ],
            card,
          ),
        EMPTY_MAP,
      ),
);

export const getCardsByGroupFromState = groupId =>
  createSelector(getCardsFromState, cards =>
    cards
      .groupBy(card => card.get("groupId").toString())
      .get(groupId.toString()),
  );

export const getCardCountByGroupFromState = groupId =>
  createSelector(getCardsByGroupFromState(groupId), cards =>
    cards ? cards.size : undefined,
  );

export const getCardContainersByGroupFromState = groupId =>
  createSelector(
    state => getGroupFromState(state, groupId),
    group => (group ? group.get("cardContainersById") : undefined),
  );

export const getCardCountByCardContainerTypeFromState = groupId =>
  createSelector(
    getCardContainersByGroupFromState(groupId),
    getCardsByGroupFromState(groupId),
    (cardContainers, cards) => {
      if (cardContainers && cards) {
        return cards
          .filter(card => card.get("type") === CARD_TYPES.EMPLOYEE) // only count EmployeeCards
          .groupBy(card =>
            cardContainers.getIn([
              card.get("cardContainerId").toString(),
              "type",
            ]),
          )
          .map(cardsByCardContainerType => cardsByCardContainerType.size);
      }
    },
  );

export const createCardCountByCardContainerTypeSelector = (
  cardCountByCardContainerSelector,
  cardContainerType,
) => {
  return createSelector(
    cardCountByCardContainerSelector,
    cardCountByCardContainerType =>
      cardCountByCardContainerType
        ? cardCountByCardContainerType.get(cardContainerType, 0)
        : 0,
  );
};

export const getCardByCardContainerAndSlotNoFromState = (
  cardContainerId,
  slotNo,
) =>
  createSelector(
    getCardsByCardContainerAndSlotNo,
    cardsByCardContainerAndSlotNo =>
      cardsByCardContainerAndSlotNo.getIn([
        cardContainerId.toString(),
        slotNo.toString(),
      ]),
  );

export const getDefaultViewIdFromState = createSelector(
  state => state.getIn(["view", "defaultViewId"]),
  defaultViewId => defaultViewId,
);

export const getViewFromState = createSelector(
  (state, viewId) => state.getIn(["view", viewId.toString()]),
  viewGroupIdsByTypeAndColumn => viewGroupIdsByTypeAndColumn,
);

export const getVisibleGroupsFromState = createSelector(
  getViewGroupsSelector,
  getGroupsSelector,
  (viewGroups, groups) =>
    groups
      .filter(group =>
        viewGroups
          .filter(viewGroup => viewGroup.get("groupId"))
          .some(viewGroup => viewGroup.get("groupId") === group.get("id")),
      )
      .toList(),
);

export const getVisibleCardsFromState = createSelector(
  getCardsFromState,
  getVisibleGroupsFromState,
  (cards, visibleGroups) =>
    visibleGroups.reduce(
      (matchingCards, group) =>
        matchingCards.concat(
          cards
            .filter(card => card.get("groupId") === group.get("id"))
            .toList(),
        ),
      EMPTY_LIST,
    ),
);

export const getGroupsByGroupTypeFromState = type =>
  createSelector(
    getGroupsFromState,
    getStationaryAndMobileFacilitiesFromState,
    (groups, facilities) =>
      groups
        .groupBy(group => group.get("type").toString())
        .get(type.toString(), EMPTY_LIST)
        .map(group => {
          const facilityId = group.get("facilityId");

          const facility = facilityId
            ? facilities.getIn([
                group.get("facilityType").toString(),
                facilityId.toString(),
              ])
            : FacilityMapper.initial();

          return group.set("facility", facility);
        }),
  );

export const getLocationGroupsFromState = getGroupsByGroupTypeFromState(
  GROUP_TYPES.LOCATION_GROUP,
);

export const getPlanningGroupsFromState = getGroupsByGroupTypeFromState(
  GROUP_TYPES.PLANNING_GROUP,
);

const getEmployeeGroupsSelector = state => state.getIn(["employeeGroups"]);

export const getEmployeeGroupsFromState = createSelector(
  getEmployeeGroupsSelector,
  employeeGroups => employeeGroups.toList(),
);

export const getEmployeeGroupsByIdFromState = createSelector(
  getEmployeeGroupsSelector,
  employeeGroups => employeeGroups,
);

const getSummarizedCardCompetencyStatusSelector = (state, groupId, cardId) =>
  state.getIn([
    "cardCompetencyStatus",
    "summarized",
    groupId.toString(),
    cardId.toString(),
    "competencyStatus",
  ]);

export const getSummarizedCardCompetencyStatusFromState = createSelector(
  getSummarizedCardCompetencyStatusSelector,
  status => status,
);

export const getCardMissingCompetencyStatusItemsFromState = createSelector(
  (state, groupId, cardId) =>
    state.getIn([
      "cardCompetencyStatus",
      "byGroupAndCardId",
      groupId.toString(),
      cardId.toString(),
    ]),
  competencyStatusItems => competencyStatusItems,
);

export const getWorkDaysFromState = createSelector(
  (state, date) => state.getIn(["workDays", date]),
  workDays => workDays,
);

export const getSubContractorsFromState = createSelector(
  getCardsSelector,
  subContractors => (subContractors ? subContractors.toList() : undefined),
);

export const getSubContractorFromState = createSelector(
  (state, cardId) => state.getIn(["cards", cardId.toString()]),
  subContractor => subContractor,
);

export const getViewGroupsIdsForGroupTypeFromState = createSelector(
  (state, type) =>
    state.getIn(["viewGroups", "byTypeAndColumn", type.toString()]),
  viewGroups => viewGroups,
);

export const getViewGroupIdsFromState = createSelector(
  state => state.getIn(["viewGroups", "byTypeAndColumn"]),
  viewGroupIds => viewGroupIds,
);

export const getViewGroupIdsForGroupTypeAndColumnFromState = createSelector(
  (state, groupType, viewColumn) =>
    state.getIn([
      "viewGroups",
      "byTypeAndColumn",
      groupType.toString(),
      viewColumn,
    ]),
  viewGroupIds => viewGroupIds,
);

// TODOA Get from projects

export const getGroupProjectsFromState = createSelector(
  (state, groupId) =>
    state.getIn(["projects", "projectsByGroup", groupId.toString()]),
  projects => projects,
);
